import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import axiosInstance from '../config/axios';
import { Facility } from '../interfaces/facility';
import { BasicModeAdvisoryReason } from 'interfaces/basic-mode-advisory-reason';

/**
 * @description Basic mode advisory reason response type
 * @type {BasicModeAdvisoryReasonResponse}
 */
export type BasicModeAdvisoryReasonResponse = {
  records: BasicModeAdvisoryReason[];
  pageIndex: number;
  pageSize: number;
  totalPages: number;
};

/**
 * @description Basic mode advisory reason service
 * @type {BasicModeAdvisoryReasonService}
 */
const BasicModeAdvisoryReasonService = {
  /**
   * @description Create basic mode advisory reason
   * @param {Partial<BasicModeAdvisoryReason>} data - Basic mode advisory reason data
   * @returns {Promise<BasicModeAdvisoryReason>} Basic mode advisory reason
   */
  create: (data: Partial<BasicModeAdvisoryReason>) => {
    return axiosInstance.post<BasicModeAdvisoryReason>(
      '/v1/basic-mode-advisory-reasons',
      data,
    );
  },

  /**
   * @description Update basic mode advisory reason
   * @param {string | number} id - Basic mode advisory reason id
   * @param {Partial<BasicModeAdvisoryReason>} data - Basic mode advisory reason data
   * @returns {Promise<BasicModeAdvisoryReason>} Basic mode advisory reason
   */
  update: (id: string | number, data: Partial<BasicModeAdvisoryReason>) => {
    return axiosInstance.patch<Facility>(
      `/v1/basic-mode-advisory-reasons/${id}`,
      data,
    );
  },

  /**
   * @description Find basic mode advisory reason
   * @param {string | number} id - Basic mode advisory reason id
   * @returns {Promise<BasicModeAdvisoryReason>} Basic mode advisory reason
   */
  find: (id: string | number) => {
    return axiosInstance.get<BasicModeAdvisoryReason>(
      `/v1/basic-mode-advisory-reasons/${id}`,
    );
  },

  /**
   * @description Delete basic mode advisory reason
   * @param {string | number} id - Basic mode advisory reason id
   * @returns {Promise<BasicModeAdvisoryReason>} Basic mode advisory reason
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<BasicModeAdvisoryReason>(
      `/v1/basic-mode-advisory-reasons/${id}`,
    );
  },

  /**
   * @description Get basic mode advisory reasons
   * @param {Object} queryParams - Query parameters
   * @returns {Promise<BasicModeAdvisoryReasonResponse>} Basic mode advisory reason response
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters?: ColumnFilter[];
  }) => {
    return axiosInstance.get<BasicModeAdvisoryReasonResponse>(
      '/v1/basic-mode-advisory-reasons',
      {
        params: queryParams,
      },
    );
  },
};

export default BasicModeAdvisoryReasonService;
